import React, { memo, useEffect, useState } from 'react';
import { Button, Typography } from '@mui/material';
import Add from '@mui/icons-material/Add';

import { CardWrapper, useHookForm } from '@monorepo/common';

import { ArrowRight as ArrowRightIcon } from '../../../Icons/arrow-right';
import { IEventsStepProps } from '../../interfaceSteps';
import { step3ItemLocation } from '../../staticValues';
import LocationTitle from '../../components/LocationTitle';
import { IStep3Form } from './formInterfaces';
import Location from './components/Location';
import Salary from './components/Salary';
import { schema } from './schema';
import { ServerFieldErrors, useServerErrors } from '../../useServerErrors';

interface IStep3Props extends IEventsStepProps {
  defaultData: IStep3Form
  setStep3: (val: IStep3Form) => void
  serverErrors?: ServerFieldErrors
}

export const Step3 = memo(({ onNext, onBack, Nav, defaultData, setStep3, serverErrors }:IStep3Props) => {
  const [isShowLocation, setIsShowLocation] = useState(false);
  const [changedFields, setChangedFields] = useState<IStep3Form | null>(null);

  const { register, errors, getValues, setValue, setError, control, handleSubmit, useFieldArray } = useHookForm<IStep3Form>({
    schema,
    defaultValues: defaultData,
    showChangedFields: (a) => {
      setChangedFields(a);
    }
  });

  useServerErrors(setError, serverErrors);

  const goBack = () => {
    if (onBack) {
      if (changedFields && (JSON.stringify(defaultData).toString() !== JSON.stringify(changedFields))) {
        setStep3(changedFields);
      }
      onBack();
    }
  };

  const { fields, prepend, remove } = useFieldArray({
    name: 'locations',
    control
  });

  const increase = () => {
    prepend(step3ItemLocation);
  };

  const decrease = (id: number) => {
    if (fields.length === 1) {
      setValue('locations', [step3ItemLocation]);
      return;
    }
    remove(id);
  };
  const onSubmit = (data: IStep3Form) => {
    if (data && onNext) {
      setStep3(data);
      onNext();
    }
  };

  const toggleLocation = (status: boolean) => {
    setIsShowLocation(!status);
    if (!isShowLocation) {
      increase();
    }
  };

  useEffect(() => {
    setIsShowLocation(!defaultData.noneLocation);
    const timeout = setTimeout(() => {
      defaultData.locations.forEach((item) => {
        prepend(item);
      });
      clearTimeout(timeout);
    }, 0);
  }, [defaultData]);

  useEffect(() => {
    setValue('noneLocation', !isShowLocation);
    if (!isShowLocation) {
      setValue('locations', []);
    }
  }, [isShowLocation]);

  return (
    <CardWrapper
      classNameForInternalBlock="cpg-step-card"
      scrollOptions={{
        isErrorsAutoScrollPage: errors
      }}
    >
      <form
        data-cy="step3"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Salary
          defaultValueMax={defaultData.salaryMax}
          defaultValueMin={defaultData.salaryMin}
          defaultValueExact={defaultData.salaryExact}
          defaultValueSalaryUnitId={defaultData.salaryUnitId}
          defaultValueRadio={defaultData.salaryUnitRadio}
          errors={errors}
          setValue={setValue}
          getValues={getValues}
          register={register}
        />

        <LocationTitle
          value={!isShowLocation}
          toggleLocation={toggleLocation}
        />

        {/* Array-level message from the schema's locations.min(1) rule. The rows
            below only render errors.locations[index], so without this the submit
            would be blocked with nothing on screen to explain why. RHF puts a
            `message` on errors.locations only for whole-array failures; per-item
            failures make it a sparse array instead. */}
        {isShowLocation && errors?.locations?.message && (
          <Typography
            variant="body2"
            color="error"
            sx={{ mb: 2 }}
          >
            {errors.locations.message}
          </Typography>
        )}

        {isShowLocation && (
        <>
          {fields.map((item, index) => (
            <Location
              key={item.id}
              register={register}
              fieldLength={fields.length || 0}
              pIsShowState={`locations.${index}.isShowState` as const}
              pAddress1={`locations.${index}.address1` as const}
              pAddress2={`locations.${index}.address2` as const}
              pCity={`locations.${index}.city` as const}
              pState={`locations.${index}.state` as const}
              pZip={`locations.${index}.zip` as const}
              pProvince={`locations.${index}.province` as const}
              pCountry={`locations.${index}.country` as const}
              decrease={decrease}
              id={index}
              setValue={setValue}
              errors={{
                ...(errors?.locations) && ({ ...errors.locations[index] })
              }}
              getValues={getValues}
            />
          ))}
        </>
        )}

        {isShowLocation && (
        <Button
          className="cpg-ghost-btn"
          sx={{ mt: 2 }}
          type="button"
          variant="outlined"
          color="info"
          startIcon={<Add />}
          onClick={() => {
            increase();
          }}
        >
          Add an additional location
        </Button>
        )}
        <Nav
          nextDisabled={false}
          onBack={goBack}
          textPreviousStep="← Back"
          nextButton={
              (
                <Button
                  endIcon={(<ArrowRightIcon fontSize="small" />)}
                  variant="contained"
                  disabled={false}
                  type="submit"
                  data-cy="nextStep"
                >
                  Next step
                </Button>
              )
            }
        />
      </form>
    </CardWrapper>
  );
});
